// Security settings — manage passkeys (WebAuthn) for the logged-in user
import { useState, useEffect, useCallback } from 'react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Fingerprint, Shield, Trash2, Plus, Loader2, KeyRound, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import portalApi from '../lib/api';
import { toast } from 'sonner';

export default function SecurityPage() {
  const [loading, setLoading] = useState(true);
  const [passkeys, setPasskeys] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const supported = browserSupportsWebAuthn();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await portalApi.listPasskeys();
      setPasskeys(data.passkeys || []);
      setConfigured(data.webauthn_configured);
    } catch (err) {
      toast.error(err.message || 'Could not load passkeys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    setAdding(true);
    try {
      const options = await portalApi.passkeyRegisterOptions();
      const attestation = await startRegistration({ optionsJSON: options });
      await portalApi.passkeyRegisterVerify(attestation, label.trim() || 'Passkey');
      toast.success('Passkey added');
      setAddOpen(false);
      setLabel('');
      load();
    } catch (err) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Passkey setup was cancelled or timed out.'
        : (err.message || 'Could not add passkey');
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await portalApi.deletePasskey(deleteTarget.id);
      toast.success('Passkey removed');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.message || 'Could not remove passkey');
    }
  };

  return (
    <div className="max-w-3xl" data-testid="security-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Shield className="h-6 w-6 text-sky-600" /> Security
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Manage the passkeys used to protect your account.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
              <Fingerprint className="h-6 w-6 text-sky-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Passkeys</h2>
              <p className="text-sm text-slate-500 mt-0.5 max-w-md">
                A passkey is an extra, optional layer added after your password and
                authenticator code. It uses your device's fingerprint, face, or PIN.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setAddOpen(true)}
            disabled={!configured || !supported}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="add-passkey-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add passkey
          </Button>
        </div>

        {!supported && (
          <div className="mt-5 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2 text-amber-700 text-sm" data-testid="passkey-unsupported">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            This browser does not support passkeys.
          </div>
        )}
        {supported && !configured && (
          <div className="mt-5 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2 text-amber-700 text-sm" data-testid="passkey-not-configured">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            Passkeys are not configured for this environment yet.
          </div>
        )}

        <div className="mt-6 border-t border-slate-100 pt-5">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
          ) : passkeys.length === 0 ? (
            <div className="text-center py-8" data-testid="no-passkeys">
              <KeyRound className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">You haven't added any passkeys yet.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="passkey-list">
              {passkeys.map((pk) => (
                <li key={pk.id} className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50/60" data-testid={`passkey-row-${pk.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Fingerprint className="h-5 w-5 text-sky-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{pk.label}</p>
                      <p className="text-xs text-slate-500">
                        Added {pk.created_at ? new Date(pk.created_at).toLocaleDateString() : '—'}
                        {pk.last_used_at ? ` · Last used ${new Date(pk.last_used_at).toLocaleDateString()}` : ' · Never used'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => setDeleteTarget(pk)}
                    className="text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                    data-testid={`delete-passkey-${pk.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!adding) setAddOpen(o); }}>
        <DialogContent data-testid="add-passkey-dialog">
          <DialogHeader>
            <DialogTitle>Add a passkey</DialogTitle>
            <DialogDescription>
              Give it a name you'll recognize, then follow your device's prompt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Passkey name</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. My MacBook, iPhone, YubiKey"
              data-testid="passkey-label-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
            <Button onClick={handleAdd} disabled={adding} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="confirm-add-passkey-btn">
              {adding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Fingerprint className="h-4 w-4 mr-1" />}
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-passkey-dialog">
          <DialogHeader>
            <DialogTitle>Remove passkey?</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.label}" will no longer be usable to sign in. You can add it again later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-passkey-btn">
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
